import { BaseQueryFn, MutationDefinition } from "@reduxjs/toolkit/dist/query";
import { MutationTrigger } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { growlInfo, growlWarning } from "@siba/utilities";
import { Method } from "axios";
import { useState } from "react";
import { ShowInfoConfirmation, ShowWarningConfirmation, ShowWarningNotification } from "@siba/components";
import { ISearchForm } from "../../Components/SearchHeader/SearchHeader";
import { queryResponse } from "../../features/services/backends/sibaApis";
import { useForm } from "react-hook-form";
import { userManagementUtil } from "@siba/utilities";
/***
 *  defines the base properties in every siba model.
 */
interface BaseSetupModel {
    id?: string;
    code?: string;
    createdBy?: string;
    modifiedBy?: string;
    createdDate?: string;
    name?: string;
    modifiedDate?: string;
    [key: string]: any;
}

/**
 * interface IGenericFormProps
 * defines properties used by the @see useGenericFormMethods
 * to generate the form functions based on any type.
 *
 * @props formName. is dispayed as the name of the form.
 * @props SaveMethod. defines the save endpoint
 * @props deleteMethod. defines the delete endpoint
 * @props updateMethod. defines the update endpoints.
 * @props searchMethod. defines the search endpoints.
 */

interface IGenericFormProps<T extends BaseSetupModel> {
    formName?: string;
    saveMethod?: MutationTrigger<
        MutationDefinition<
            T[],
            BaseQueryFn<
                {
                    url: string;
                    method: Method | undefined;
                    data?: any;
                },
                unknown,
                unknown,
                {},
                {}
            >,
            never,
            queryResponse<any>,
            string
        >
    >;
    deleteMethod?: any;
    updateMethod?: MutationTrigger<
        MutationDefinition<
            T,
            BaseQueryFn<
                {
                    url: string;
                    method: Method | undefined;
                    data?: any;
                },
                unknown,
                unknown,
                {},
                {}
            >,
            never,
            queryResponse<number>,
            string
        >
    >;
    searchMethod?: any;
    getAllMethod?: any;
    getByIdMethod?: any;
    parent?: any;
    foreignKeyToParent?: string;
    enforceConstraint?: boolean;
    isParent?: boolean;
    isWorkflow?: boolean;
    wfSchemaCode?: string;
    defaultFormValues?: any;
    clearFormOnSave?: boolean;
}
/***
 * useGenericFormMethods defines a custom hook that accepts a type and some properties and returns
 *  generic methods and functions for a  form. this includes click events that handle save, update, delete, clear.
 *  this method also handles model deifinition and model changes.
 */
export const useGenericFormMethods = <T extends BaseSetupModel>(props: IGenericFormProps<T>) => {
    const {
        saveMethod,
        formName,
        deleteMethod,
        updateMethod,
        searchMethod,
        getAllMethod,
        getByIdMethod,
        parent,
        foreignKeyToParent,
        enforceConstraint,
        isParent = false,
        isWorkflow = false,
        wfSchemaCode,
        defaultFormValues = {},
        clearFormOnSave = false
    } = props;
    const newModelInstance = {} as T;
    const [modalTitle, setModalTitle] = useState<string>("");
    const [model, setModel] = useState<T>(newModelInstance);
    const [saveType, setSaveType] = useState<string>("");
    const [modalOpen, setOpen] = useState(false);
    const [modelCollection, setModelCollection] = useState([] as T[]);

    const formHandler = useForm({ defaultValues: defaultFormValues || {} });

    /**
     *  defining an object to
     * update the useform hook object.
     * which should update the inputs.
     * @param obj
     */
    // const fillForm = (obj: typeof model) => {
    //   const objKeys = Object.keys(obj);
    //   objKeys.forEach((k) => {
    //     const key = k as keyof T;
    //     if (k && obj[key]) formHandler.setValue(k, obj[key]);
    //   });
    // };
    /**
     * Clear form callback function
     * @returns Void
     */
    const HandleClearGrid = () => {
        setModel(newModelInstance);
        formHandler.reset(defaultFormValues || {});
        formHandler.reset(defaultFormValues || newModelInstance);
        setModelCollection([]);
    };

    /***
     * Async Delete Function
     * @returns Void.
     * ***/
    // Remove Button Handler
    const HandleDelete = async () => {
        let newData: T = JSON.parse(JSON.stringify(modelCollection.filter((x) => x.id == model.id)[0]));

        newData.modifiedBy = userManagementUtil.getUserCode();
        const response: queryResponse<any> | any = await deleteMethod(newData);

        if (response.error) return;
        setModelCollection([...modelCollection.filter((x) => x.id !== model.id)]);

        //return growlInfo(`Selected record deleted`);
    };

    const handleClickOpen = async () => {
        setModalTitle(`Add ${formName}`);
        setModel(newModelInstance);
        setSaveType("save");
        await formHandler.reset(defaultFormValues || newModelInstance);
        setOpen(true);
    };
    const handleClickView = () => {
        if (!modelCollection.length) return growlInfo(`Grid is empty`);
        if (!model || model == newModelInstance) return growlInfo(`Select or Reselect from grid`);
        formHandler.reset(defaultFormValues || {});
        formHandler.reset(defaultFormValues || newModelInstance);
        formHandler.reset(model);
        setModalTitle(`View or Update ${formName}`);
        setSaveType("update");
        setOpen(true);
    };
    /**
     * Delete record callback function
     * @returns Void
     */
    const handleClickDelete = () => {
        if (!modelCollection.length) return growlInfo(`Grid is empty`);
        if (!model || model == newModelInstance) return growlInfo(`Select or Reselect from grid`);

        return ShowWarningConfirmation("Are you sure you want to delete record", HandleDelete);
    };

    /**
     * Shows notification alert when clear is clicked.
     * @returns void
     */
    const handleClickClear = () => {
        if (!modelCollection.length) return growlInfo(`Grid is empty`);
        return ShowInfoConfirmation("Are you sure you want to Clear Grid?", HandleClearGrid);
    };

    /**
     * Reset form fallback function
     * @returns Void
     */
    const resetForm = () => {
        formHandler.reset(defaultFormValues || {});
        resetWithDefaultValues();
        setModel(defaultFormValues || newModelInstance);
    };

    const resetWithDefaultValues = () => {
        if (defaultFormValues) {
            for (let i in model) {
                if (defaultFormValues[i]) {
                    formHandler.setValue(i, defaultFormValues[i]);
                } else {
                    formHandler.setValue(i, undefined as any);
                }
            }
        }
    };
    /**
     * Modal close callback function
     * @returns Void
     */
    const handleClickClose = () => {
        setOpen(false);
    };

    /**
     * Async save function
     * @returns Void
     */
    const save = async () => {
        (
            await formHandler.handleSubmit(
                (data) => {
                    ShowInfoConfirmation("Are you sure you want to Save Record?", async () => {
                        // minor validation..
                        if (data.code !== undefined) {
                            var exists = data.code ? modelCollection.filter((x) => x.code == data.code) : [];
                            if (exists.length) {
                                return growlInfo(`Code : ${model.code} exists in grid`);
                            }
                        }
                        const today = new Date().toJSON();

                        data.createdBy = userManagementUtil.getUserCode();

                        data.createdDate = today;
                        if (parent) {
                            if (parent.id && foreignKeyToParent) {
                                data[foreignKeyToParent] = parent.id;
                            } else if (enforceConstraint) {
                                return growlWarning("Parent form not saved");
                            }
                        }

                        if (isWorkflow) {
                            if (wfSchemaCode) {
                            }
                        }

                        const formData = removeEmptyFields(data) as T;
                        if (saveMethod) {
                            const response: queryResponse<T[]> | any = await saveMethod([formData]);

                            if (response.data) {
                                const newArr = [...modelCollection, ...response.data];
                                setModelCollection(newArr);

                                if (!isParent) {
                                    resetForm();
                                } else {
                                    setModel(response.data[0] || response.data);
                                    formHandler.reset(response.data[0] || response.data);
                                    setSaveType("update");
                                }
                                // clear form after saving
                                if (clearFormOnSave) {
                                    resetWithDefaultValues();
                                }
                            }
                            return response.data;
                        }
                    });
                },
                (_) => {
                    try {
                        growlWarning("please fill required inputs");
                    } catch {
                        ShowWarningNotification("please fill required inputs");
                    }
                }
            )
        )();
    };

    const saveBulk = async (collection: any[]) => {
        ShowInfoConfirmation("Are you sure you want to Save Record?", async () => {
            // minor validation..
            for (let data of collection) {
                if (data.code !== undefined) {
                    var exists = data.code ? modelCollection.filter((x) => x.code == data.code) : [];
                    if (exists.length) {
                        return growlInfo(`Code : ${model.code} exists in grid`);
                    }
                }
                const today = new Date().toJSON();

                data.createdBy = userManagementUtil.getUserCode();

                data.createdDate = today;
                if (parent) {
                    if (parent.id && foreignKeyToParent) {
                        data[foreignKeyToParent] = parent.id;
                    } else if (enforceConstraint) {
                        return growlWarning("Parent form not saved");
                    }
                }

                if (isWorkflow) {
                    if (wfSchemaCode) {
                    }
                }

                data = removeEmptyFields(data) as T;
            }
            if (saveMethod) {
                const response: queryResponse<T[]> | any = await saveMethod(collection);

                if (response.data) {
                    const newArr = [...modelCollection, ...response.data];
                    setModelCollection(newArr);

                    if (!isParent) {
                        resetForm();
                    } else {
                        setModel(response.data[0] || response.data);
                        formHandler.reset(response.data[0] || response.data);
                        setSaveType("update");
                    }
                    // clear form after saving
                    if (clearFormOnSave) {
                        resetWithDefaultValues();
                    }
                }
                return response.data;
            }
        });
    };
    /**
     * Async save function
     * @returns Void
     */
    const directSave = async () => {
        (
            await formHandler.handleSubmit(
                async (data) => {
                    // minor validation..
                    if (data.code != undefined) {
                        var exists = modelCollection.filter((x) => x.code == data.code);
                        if (exists.length) {
                            return growlInfo(`Code : ${model.code} exists in grid`);
                        }
                    }
                    const today = new Date().toJSON();

                    if (!data.createdBy) data.createdBy = "not set";

                    if (!data.createdDate) data.createdDate = today;

                    if (parent && foreignKeyToParent) {
                        data[foreignKeyToParent] = parent.id;
                    }

                    const formData = removeEmptyFields(data) as T;

                    if (saveMethod) {
                        const response: queryResponse<T[]> | any = await saveMethod([formData]);

                        resetForm();

                        if (response.data && modelCollection) {
                            const newArr = [...modelCollection, ...response.data];
                            if (modelCollection) setModelCollection(newArr);
                        }
                        if (response.data) {
                            if (!isParent) {
                                resetForm();
                            } else {
                                setModel(response.data[0] || response.data);
                                formHandler.reset(response.data[0] || response.data);
                            }
                            // clear form after saving
                            if (clearFormOnSave) {
                                resetForm();
                            }
                        }

                        return response.data;
                    }
                },
                (_) => {
                    try {
                        growlWarning("please fill required inputs");
                    } catch {
                        ShowWarningNotification("please fill required inputs");
                    }
                }
            )
        )();
    };

    /**
     * Async save function
     * @returns Void
     */
    const dataSave = async (data: any) => {
        // minor validation..
        if (data.code !== undefined) {
            var exists = modelCollection.filter((x) => x.code == data.code);
            if (exists.length) {
                return growlInfo(`Code : ${data.code} exists in grid`);
            }
        }
        const today = new Date().toJSON();

        if (!data.createdBy) data.createdBy = userManagementUtil.getUserCode();

        if (!data.createdDate) data.createdDate = today;

        if (parent && foreignKeyToParent) {
            data[foreignKeyToParent] = parent.id;
        }

        const formData = removeEmptyFields(data) as T;

        if (saveMethod) {
            const response: queryResponse<T[]> | any = await saveMethod([formData]);

            resetForm();

            if (response.data && modelCollection) {
                const newArr = [...modelCollection, ...response.data];
                if (modelCollection) setModelCollection(newArr);
                if (response.data) {
                    const newArr = [...modelCollection, ...response.data];
                    setModelCollection(newArr);

                    if (!isParent) {
                        resetForm();
                    } else {
                        setModel(response.data[0] || response.data);
                        formHandler.reset(response.data[0] || response.data);
                    }
                    // clear form after saving
                    if (clearFormOnSave) {
                        resetForm();
                    }
                }
            }

            return response.data;
        }
    };

    /**
     * Async Update function
     * @returns Void
     */
    const update = async () => {
        formHandler.handleSubmit(
            async (data) => {
                ShowInfoConfirmation("Are you sure you want to update Record?", async () => {
                    const formData = data as T;
                    if (formData === newModelInstance) return;

                    // minor Validation
                    var exists = modelCollection.filter((x) => x.id == (formData.id || model.id));

                    if (!exists.length) {
                        let msg: string = `no record found to update please select from the grid`;
                        growlWarning(msg);
                    }

                    let newData: T = JSON.parse(JSON.stringify(modelCollection.filter((x) => x.id == (formData.id || model.id))[0]));

                    const today = new Date().toJSON();

                    for (let i in formData) {
                        newData[i] = formData[i];
                    }

                    newData.modifiedBy = userManagementUtil.getUserCode();
                    newData.modifiedDate = today;

                    const updatedCollection = [...modelCollection.filter((x) => x.id !== newData.id), JSON.parse(JSON.stringify(newData))] as T[];
                    removeEmptyFields(newData);
                    if (updateMethod) {
                        const response: queryResponse<any> | any = await updateMethod(newData);
                        if (response.data) {
                            await setModelCollection(updatedCollection);
                            setModel(newData);

                            // clear form after saving
                            if (clearFormOnSave) {
                                resetWithDefaultValues();
                            }
                        }
                    }
                });
            },
            (errors) => {
                ShowWarningNotification("please fill required inputs");
            }
        )();
        // return growlInfo(`Updated Successful`);
    };

    /**
     * save handler fallback
     * @returns Void
     */
    const handleSave = (type: string = saveType) => {
        switch (type) {
            case "save":
                {
                    return save;
                }
                break;
            case "update":
                {
                    return update;
                }
                break;
            default: {
                return () => {
                    return growlInfo("Unable to save");
                };
            }
        }
    };

    /**
     * save button handler
     * @returns Void
     */
    const handleModelSearch = async (form: ISearchForm) => {
        if (searchMethod) {
            const response: queryResponse<any> | any = await searchMethod(form);
            setModelCollection([]);
            if (response.data) {
                setModelCollection(response.data as T[]);
            }
        }
    };
 /**
     * save button handler
     * @returns Void
     */
 const handleModelGetById = async (id: string) => {
    if (getByIdMethod) {
        const response: queryResponse<any> | any = await getByIdMethod(id);
        setModelCollection([]);
        if (response.data) {
            setModelCollection(response.data as T[]);
        }
    }
};
 /**
     * save button handler
     * @returns Void
     */
 const handleModelGetAll = async () => {
    if (getAllMethod) {
        const response: queryResponse<any> | any = await getAllMethod();
        setModelCollection([]);
        if (response.data) {
            setModelCollection(response.data as T[]);
        }
    }
};
    /**
     * Model onChange input handler
     * @returns Void
     */
    const handleModelFormInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setModel({ ...model, [name]: value });
    };

    /**
     * Allows double clicking on grid columns to view content
     * @returns Void
     */
    const gridDoubleClickModalResponse = () => {
        setOpen(true);
        setSaveType("update");
        setModalTitle(`View or Update ${formName}`);
        formHandler.reset(defaultFormValues || {});
        formHandler.reset(defaultFormValues || newModelInstance);
        formHandler.reset(model);
    };

    const fetchCollectionById = async (id: string, endpoint: any, reload = false) => {
        // if theere is no parent id return.
        // if (!id) return growlInfo("office information may not be saved");
        if (!id) return "";
        // if the grid is already populated, no need to fetch again, return.
        if (modelCollection.length && !reload) return;

        const response: any = await endpoint(id);

        if (response && response.data && response.data.length) {
            setModelCollection(response.data as T[]);
        }

        return response.data;
    };

    return {
        handleClickOpen,
        handleClickView,
        handleClickDelete,
        handleModelGetAll,
        handleModelGetById,
        modalOpen,
        resetForm,
        handleClickClose,
        handleClickClear,
        save,
        update,
        handleSave,
        modelCollection,
        model,
        handleModelFormInputChange,
        handleModelSearch,
        gridDoubleClickModalResponse,
        modalTitle,
        saveType,
        setModel,
        formHandler,
        directSave,
        setModelCollection,
        dataSave,
        setOpen,
        setSaveType,
        fetchCollectionById,
        saveBulk
    };
};

/****
 * Method removeEmptyField takes in an object and replaces empty string values with null
 * @params data.
 * @return
 **/
export const removeEmptyFields = (data: object | any) => {
    Object.keys(data).forEach((key) => {
        if (data[key] === "" || data[key] == null) {
            delete data[key];
        }
    });
    return data;
};

// eslint-disable-next-line no-extend-native
export const toTitleCase = function (str: string) {
    if (!str) return "";
    //("use strict");
    var smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|v.?|vs.?|via)$/i;
    var alphanumericPattern = /([A-Za-z0-9\u00C0-\u00FF])/;
    var wordSeparators = /([ :–—-])/;

    return str
        .split(wordSeparators)
        .map(function (current, index, array) {
            if (
                /* Check for small words */
                current.search(smallWords) > -1 &&
                /* Skip first and last word */
                index !== 0 &&
                index !== array.length - 1 &&
                /* Ignore title end and subtitle start */
                array[index - 3] !== ":" &&
                array[index + 1] !== ":" &&
                /* Ignore small words that start a hyphenated phrase */
                (array[index + 1] !== "-" || (array[index - 1] === "-" && array[index + 1] === "-"))
            ) {
                return current.toLowerCase();
            }

            /* Ignore intentional capitalization */
            if (current.substr(1).search(/[A-Z]|\../) > -1) {
                return current;
            }

            /* Ignore URLs */
            if (array[index + 1] === ":" && array[index + 2] !== "") {
                return current;
            }

            /* Capitalize the first letter */
            return current.replace(alphanumericPattern, function (match) {
                return match.toUpperCase();
            });
        })
        .join("");
};

export const getYearListSelectOptions = (minRange?: number, maxRange?: number) => {
    const thisYear = new Date().getFullYear();

    let yearSelectList = [] as { label: string; value: string | number | undefined | null }[];

    let minYearValue = minRange ? thisYear - minRange : thisYear - 20;
    let maxYearValue = maxRange ? thisYear + maxRange : thisYear + 10;
    while (maxYearValue > minYearValue - 1) {
        yearSelectList.push({ label: `${maxYearValue}`, value: maxYearValue });
        maxYearValue = maxYearValue - 1;
    }

    return yearSelectList;
};

export const getDatePeriod = (dt: string) => {
    const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

    const d = new Date(dt);
    return monthNames[d.getMonth()] + "-" + d.getFullYear();
};

export const gridDateFormat = (field: string) => {
    return (props: any): any => {
        if (props[field]) {
            if (props[field] instanceof Date) {
                const thisDate: Date = props[field] as Date;
                return thisDate.toLocaleString("EN-UK");
            } else if (new Date(props[field]) instanceof Date) {
                const thisDate: Date = new Date(props[field]);
                return thisDate.toLocaleString("EN-UK");
            }
            return props[field];
        }
        return props[field] || "";
    };
};
export const currencyNumberFormat = (field: string) => {
    return (props: any): any => {
        if (props[field] || props[field] == 0) {
            const amount = props[field];
            if (typeof amount == "string") {
                return parseFloat(amount as string)?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            } else if (typeof amount == "number") {
                return amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
            return props[field];
        }
        return props[field] || "";
    };
};
export const formatUKDate = (dateField: any) => {
    if (dateField) {
        if (dateField instanceof Date) {
            const thisDate: Date = dateField as Date;
            return thisDate.toLocaleString("EN-UK");
        } else if (new Date(dateField) instanceof Date) {
            const thisDate: Date = new Date(dateField);
            return thisDate.toLocaleString("EN-UK");
        }
        return dateField;
    }
    return dateField || "";
}
export const formatNumberCurrency = (amount: string | number) => {
    if (typeof amount == "string") {
        return parseFloat(amount as string)?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    } else if (typeof amount == "number") {
        return amount?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
    return amount || "";
};
